// 051: grillas de círculos impresos del papel. Cada casilla es tri-estado (Sí / No / sin responder); donde el papel imprime un
// tercer círculo ("s/d", "n/c") se ofrece como opción propia. Volver a pulsar la opción elegida la limpia.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SiNoBotones } from './camposSimples';
import type { SiNoValor } from './camposSimplesDatos';

/** Una pregunta con N círculos exclusivos (Sí / No / S/D, Sí / No / N/C…) y estado "sin responder". */
export function BotonesOpcion({
  id,
  etiqueta,
  valor,
  opciones,
  onChange,
}: {
  readonly id: string;
  readonly etiqueta: string;
  readonly valor: string;
  readonly opciones: readonly { readonly valor: string; readonly texto: string }[];
  readonly onChange: (v: string) => void;
}): JSX.Element {
  return (
    <fieldset id={id} className="flex min-w-0 flex-col gap-1 border-0 p-0">
      <legend className="p-0 text-sm font-medium">{etiqueta}</legend>
      <div className="flex flex-wrap gap-2">
        {opciones.map((o) => (
          <Button key={o.valor} type="button" size="sm" variant={valor === o.valor ? 'default' : 'outline'} aria-pressed={valor === o.valor} onClick={() => onChange(valor === o.valor ? '' : o.valor)}>
            {o.texto}
          </Button>
        ))}
      </div>
    </fieldset>
  );
}

/** Grilla no/si por clave (el papel trae las dos casillas por ítem): cada fila es un Sí/No con "sin responder". */
export function GrillaSiNo({
  titulo,
  claves,
  prefijoI18n,
  prefijoId,
  valores,
  onChange,
}: {
  readonly titulo?: string;
  readonly claves: readonly string[];
  readonly prefijoI18n: string;
  readonly prefijoId: string;
  readonly valores: Readonly<Record<string, SiNoValor>>;
  readonly onChange: (v: Readonly<Record<string, SiNoValor>>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      {titulo ? <Label>{titulo}</Label> : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        {claves.map((c) => (
          <SiNoBotones
            key={c}
            id={`${prefijoId}-${c}`}
            etiqueta={t(`${prefijoI18n}.${c}`)}
            valor={valores[c] ?? ''}
            textos={{ si: t('formulario.si'), no: t('formulario.no') }}
            onChange={(v) => onChange({ ...valores, [c]: v })}
          />
        ))}
      </div>
    </div>
  );
}

/** Grilla Sí / No / S/D por clave (INTERVENCIONES del papel). */
export function GrillaSiNoSd({
  claves,
  prefijoI18n,
  prefijoId,
  valores,
  onChange,
}: {
  readonly claves: readonly string[];
  readonly prefijoI18n: string;
  readonly prefijoId: string;
  readonly valores: Readonly<Record<string, string>>;
  readonly onChange: (v: Readonly<Record<string, string>>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const opciones = [
    { valor: 'si', texto: t('formulario.si') },
    { valor: 'no', texto: t('formulario.no') },
    { valor: 'sd', texto: t('formularios.f051.nearMiss.opcionesLaboratorio.sd') },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
      {claves.map((c) => (
        <BotonesOpcion key={c} id={`${prefijoId}-${c}`} etiqueta={t(`${prefijoI18n}.${c}`)} valor={valores[c] ?? ''} opciones={opciones} onChange={(v) => onChange({ ...valores, [c]: v })} />
      ))}
    </div>
  );
}
